import {
    Button,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { Link, Outlet, useNavigate, useOutletContext } from 'react-router';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    Add,
    AutoMode,
    CheckCircleOutlined,
    Delete,
    Edit,
    HighlightOff,
    RemoveCircleOutlineOutlined,
    Schedule,
} from '@mui/icons-material';
import { type JSX, useState } from 'react';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import {
    EmptyResults,
    FetchError,
    ResultLimitAlert,
} from '../shared-mui-components/ListMessages.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { SeparationBar } from '../layout/components/SeparationBar.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { RecodeCondition } from './CloudConnectorRecodeQrCodesAdd.tsx';

interface AutoRecodesResponse {
    autoRecodes: AutoRecode[];
    eof: boolean;
}

export enum AutoRecodeStatus {
    FUTURE = 'FUTURE',
    ACTIVE = 'ACTIVE',
    EXPIRED = 'EXPIRED',
    DELETED = 'DELETED',
}

interface AutoRecode {
    autoRecodeId: string;
    name: string;
    recodeCondition: RecodeCondition;
    validFrom: string;
    validTo: string;
    status: AutoRecodeStatus;
}

export function CloudConnectorAutoRecode() {
    const navigate = useNavigate();
    const [autoRecodesListState, refetchAutoRecodesList] = useServerFetch<
        AutoRecodesResponse,
        object
    >(
        () => ({
            url: `/ui-api/operator-account/cloud-connector/recode/auto-recode`,
        }),
        {},
    );

    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <Localized
                        de="Autom. Rabatt"
                        fr="Rabais autom."
                        it="Ribasso autom."
                        en="Auto disc."
                    />
                }
                icon={AutoMode}
                headerButtons={
                    <Stack
                        direction="row"
                        spacing={3}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Link to={'add'}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                color="primary"
                            >
                                <Localized
                                    de="Neue Periode"
                                    fr="Nouvelle période"
                                    it="Nuovo periodo"
                                    en="New period"
                                />
                            </Button>
                        </Link>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('../statement')}
                        >
                            <Localized
                                de="Schliessen"
                                fr="Fermer"
                                it="Chiudi"
                                en="Close"
                            />
                        </Button>
                    </Stack>
                }
            />
            <SeparationBar />
            <AutoRecodesList
                autoRecodesListState={autoRecodesListState}
                refetchAutoRecodesList={refetchAutoRecodesList}
                onEdit={(autoRecodeId: string) =>
                    navigate(`${autoRecodeId}/edit`)
                }
            />
        </MainContent>
    );
}

function AutoRecodesList({
    autoRecodesListState,
    refetchAutoRecodesList,
    onEdit,
}: {
    autoRecodesListState: ServerRequestState<
        AutoRecodesResponse,
        AutoRecodesResponse
    >;
    refetchAutoRecodesList: () => void;
    onEdit: (autoRecodeId: string) => void;
}) {
    return (
        <OperatorAsyncLoadedSection
            requestState={autoRecodesListState}
            pendingLoaderPreset={LoadingSpinnerPresets.FillAllSpaceAndCenter}
            render={(autoRecodeResponse: AutoRecodesResponse) => (
                <>
                    <AutoRecodesListContent
                        autoRecodeResponse={autoRecodeResponse}
                        refetchAutoRecodesList={refetchAutoRecodesList}
                        onEdit={onEdit}
                    />
                    <AutoRecodeOutlet
                        context={{
                            autoRecodes: autoRecodeResponse.autoRecodes,
                            refetchAutoRecodesList: refetchAutoRecodesList,
                        }}
                    />
                </>
            )}
            renderError={() => (
                <FetchError refetchList={refetchAutoRecodesList} />
            )}
        />
    );
}

function AutoRecodesListContent({
    autoRecodeResponse,
    refetchAutoRecodesList,
    onEdit,
}: {
    autoRecodeResponse: AutoRecodesResponse;
    refetchAutoRecodesList: () => void;
    onEdit: (autoRecodeId: string) => void;
}) {
    const [deleteAutoRecodeInfo, setDeleteAutoRecodeInfo] =
        useState<AutoRecode | null>(null);

    const [deleteAutoRecodeState, deleteAutoRecode] = useServerWrite<
        { autoRecodeId: string },
        null,
        ValidationData
    >(({ autoRecodeId }) => ({
        url: `/ui-api/operator-account/cloud-connector/recode/auto-recode/${autoRecodeId}`,
        method: RequestMethod.DELETE,
    }));

    useServerSuccessEffect(deleteAutoRecodeState, () => {
        refetchAutoRecodesList();
        setDeleteAutoRecodeInfo(null);
    });

    useServerErrorEffect(deleteAutoRecodeState, () => {
        setDeleteAutoRecodeInfo(null);
    });

    const isEmpty = autoRecodeResponse.autoRecodes.length === 0;

    if (isEmpty) {
        return <EmptyResults />;
    }

    return (
        <>
            <BackendRequestErrorMessage requestState={deleteAutoRecodeState} />
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Localized
                                    de="Status"
                                    fr="Status"
                                    it="Status"
                                    en="Status"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Bezeichnung"
                                    fr="Description"
                                    it="Descrizione"
                                    en="Description"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Rabattbedingung"
                                    fr="Condition de rabais"
                                    it="Condizione ribasso"
                                    en="Discount condition"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Beginn"
                                    fr="Début"
                                    it="Inizio"
                                    en="Start"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Ende"
                                    fr="Fin"
                                    it="Fine"
                                    en="End"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <Localized
                                    de="Aktionen"
                                    fr="Actions"
                                    it="Azioni"
                                    en="Actions"
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {autoRecodeResponse.autoRecodes.map(
                            (autoRecode: AutoRecode) => {
                                return (
                                    <TableRow key={autoRecode.autoRecodeId}>
                                        <TableCell>
                                            <IconForAutoRecodeStatus
                                                autoRecodeStatus={
                                                    autoRecode.status
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>{autoRecode.name}</TableCell>
                                        <TableCell>
                                            <Localized
                                                {...autoRecode.recodeCondition
                                                    .description}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {Formatter.dayMonthYearHourMinute(
                                                DateTime.fromISO(
                                                    autoRecode.validFrom,
                                                ),
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {Formatter.dayMonthYearHourMinute(
                                                DateTime.fromISO(
                                                    autoRecode.validTo,
                                                ),
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {(autoRecode.status ===
                                                AutoRecodeStatus.FUTURE ||
                                                autoRecode.status ===
                                                    AutoRecodeStatus.ACTIVE) && (
                                                <Tooltip
                                                    title={
                                                        <Localized
                                                            de="editieren"
                                                            fr="modifier"
                                                            it="modifica"
                                                            en="edit"
                                                        />
                                                    }
                                                >
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            onEdit(
                                                                autoRecode.autoRecodeId,
                                                            )
                                                        }
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {autoRecode.status ===
                                                AutoRecodeStatus.FUTURE && (
                                                <Tooltip
                                                    title={
                                                        <Localized
                                                            de="löschen"
                                                            fr="supprimer"
                                                            it="elimina"
                                                            en="delete"
                                                        />
                                                    }
                                                >
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            setDeleteAutoRecodeInfo(
                                                                autoRecode,
                                                            )
                                                        }
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            },
                        )}
                    </TableBody>
                </Table>
                <ResultLimitAlert
                    eof={autoRecodeResponse.eof}
                    numberOfResults={autoRecodeResponse.autoRecodes.length}
                    hasExport={false}
                />
                {deleteAutoRecodeInfo !== null && (
                    <DeleteAutoRecodeModal
                        autoRecode={deleteAutoRecodeInfo}
                        deleteAutoRecodeState={deleteAutoRecodeState}
                        onDelete={() =>
                            deleteAutoRecode({
                                autoRecodeId: deleteAutoRecodeInfo.autoRecodeId,
                            })
                        }
                        onClose={() => setDeleteAutoRecodeInfo(null)}
                    />
                )}
            </TableContainer>
        </>
    );
}

function DeleteAutoRecodeModal({
    autoRecode,
    deleteAutoRecodeState,
    onDelete,
    onClose,
}: {
    autoRecode: AutoRecode;
    deleteAutoRecodeState: ServerRequestState<null, ValidationData>;
    onDelete: () => void;
    onClose: () => void;
}) {
    return (
        <ConfirmationDialog
            open={true}
            title={
                <Localized
                    de="Rabatt-Periode löschen"
                    fr="Supprimer la période de rabais"
                    it="Eliminare il periodo di ribasso"
                    en="Delete discount period"
                />
            }
            confirmLabel={
                <Localized
                    de="Löschen"
                    fr="Effacher"
                    it="Elimina"
                    en="Delete"
                />
            }
            confirmDisabled={
                deleteAutoRecodeState.status === RequestStatus.PENDING
            }
            onConfirm={onDelete}
            onCancel={onClose}
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de={`Möchten Sie die automatische Rabatt-Periode ${autoRecode?.name} löschen?`}
                    fr={`Souhaitez-vous supprimer la période de rabais automatique ${autoRecode?.name}?`}
                    it={`Desideri eliminare il periodo di ribasso automatico ${autoRecode?.name}?`}
                    en={`Would you like to delete the automatic discount period ${autoRecode?.name}?`}
                />
            </p>
        </ConfirmationDialog>
    );
}

function IconForAutoRecodeStatus({
    autoRecodeStatus,
}: {
    autoRecodeStatus: AutoRecodeStatus;
}): JSX.Element {
    switch (autoRecodeStatus) {
        case AutoRecodeStatus.FUTURE:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="zukünftig gültig"
                            fr="Valable dans le futur"
                            it="valido in futuro"
                            en="Valid in the future"
                        />
                    }
                    placement="right"
                >
                    <Schedule color="warning" />
                </Tooltip>
            );
        case AutoRecodeStatus.ACTIVE:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="gültig"
                            fr="valable"
                            it="valido"
                            en="valid"
                        />
                    }
                    placement="right"
                >
                    <CheckCircleOutlined color="success" />
                </Tooltip>
            );
        case AutoRecodeStatus.EXPIRED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="abgelaufen"
                            fr="expirée"
                            it="scaduta"
                            en="expired"
                        />
                    }
                    placement="right"
                >
                    <RemoveCircleOutlineOutlined color="disabled" />
                </Tooltip>
            );
        case AutoRecodeStatus.DELETED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="gelöscht"
                            fr="supprimé"
                            it="cancellato"
                            en="deleted"
                        />
                    }
                    placement="right"
                >
                    <HighlightOff color="error" />
                </Tooltip>
            );
    }
}

export function useAutoRecodeOutlet() {
    return useOutletContext<AutoRecodeOutletContext>();
}

export function AutoRecodeOutlet({
    context,
}: {
    context: AutoRecodeOutletContext;
}) {
    return <Outlet context={context} />;
}

export interface AutoRecodeOutletContext {
    autoRecodes: AutoRecode[];
    refetchAutoRecodesList: () => void;
}
